import {
  CreateInspectionRequest,
  MediaItem,
  DataWithMedia,
  RoadCondition,
  WeatherCondition,
} from '@property/shared';
import { InspectionFormData, PendingMediaItem } from '@/types/inspection-form';

/**
 * Transform PendingMediaItem to MediaItem (after upload)
 */
function pendingToMediaItem(
  pending: PendingMediaItem,
  propertyId: string,
  inspectionId: string,
): MediaItem {
  if (!pending.url) {
    throw new Error(`Media item ${pending.id} has no URL`);
  }

  return {
    id: pending.id,
    type: pending.type,
    label: pending.label,
    url: pending.url,
    durationSec: pending.durationSec,
    propertyId,
    inspectionId,
    gpsTagged: pending.gpsTagged,
    latitude: pending.latitude,
    longitude: pending.longitude,
  };
}

/**
 * Transform DataWithMedia field from form data
 */
function transformDataWithMedia<T extends string>(
  field: { value: T | ''; media: PendingMediaItem[] },
  propertyId: string,
  inspectionId: string,
): DataWithMedia<T> {
  return {
    value: field.value as T,
    media:
      field.media.length > 0
        ? field.media.map((m) => pendingToMediaItem(m, propertyId, inspectionId))
        : undefined,
  };
}

/**
 * Transform InspectionFormData to CreateInspectionRequest
 * This is called after all media has been uploaded
 */
export function transformToCreateInspectionRequest(
  formData: InspectionFormData,
  taskId: string,
  propertyId: string,
  inspectionId: string,
): CreateInspectionRequest {
  return {
    taskId,
    registeredOwnerName: formData.registeredOwnerName,
    detectedOwnerName: formData.detectedOwnerName || undefined,
    issues:
      formData.issues.length > 0
        ? formData.issues
            .filter((issue) => issue.title.trim() && issue.severity)
            .map((issue) => ({
              title: issue.title,
              severity: issue.severity as 'low' | 'medium' | 'high',
              remarks: issue.remarks,
            }))
        : undefined,

    // Weather (just value, no media)
    weather: formData.weather as WeatherCondition,

    // Road condition (type + rating, no media)
    roadCondition: {
      type: formData.roadCondition.type,
      rating: formData.roadCondition.rating,
    } as RoadCondition,

    // DataWithMedia fields
    drainStatus: transformDataWithMedia(formData.drainStatus, propertyId, inspectionId),
    ownershipNotice: transformDataWithMedia(formData.ownershipNotice, propertyId, inspectionId),
    wasteDumping: transformDataWithMedia(formData.wasteDumping, propertyId, inspectionId),
    vegetationStatus: transformDataWithMedia(formData.vegetationStatus, propertyId, inspectionId),
    waterloggedStatus: transformDataWithMedia(formData.waterloggedStatus, propertyId, inspectionId),
    constructionActivity: transformDataWithMedia(
      formData.constructionActivity,
      propertyId,
      inspectionId,
    ),

    // Transform media arrays
    mainAccessPoint: formData.mainAccessPoint.map((m) =>
      pendingToMediaItem(m, propertyId, inspectionId),
    ),
    siteOverview: formData.siteOverview.map((m) => pendingToMediaItem(m, propertyId, inspectionId)),
  };
}

/**
 * Get all media items from form data for batch upload
 */
export function getAllMediaItems(formData: InspectionFormData): PendingMediaItem[] {
  const mediaItems: PendingMediaItem[] = [];

  // Pure media arrays
  mediaItems.push(...formData.mainAccessPoint);
  mediaItems.push(...formData.siteOverview);

  // DataWithMedia fields
  mediaItems.push(...formData.drainStatus.media);
  mediaItems.push(...formData.ownershipNotice.media);
  mediaItems.push(...formData.wasteDumping.media);
  mediaItems.push(...formData.vegetationStatus.media);
  mediaItems.push(...formData.waterloggedStatus.media);
  mediaItems.push(...formData.constructionActivity.media);

  return mediaItems;
}
